import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import { useT } from '../../i18n/LocaleContext';
import { classNames } from '../../utils/format';

export default function RoleFormModal({ isOpen, onClose, onSubmit, initial, allPermissions = [] }) {
  const t = useT();
  const isEdit = Boolean(initial);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(initial?.name || '');
    setDescription(initial?.description || '');
    setSelected(new Set(initial?.permissions || []));
    setErrors({});
  }, [isOpen, initial]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of allPermissions) {
      const g = p.group || 'other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(p);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [allPermissions]);

  const toggle = (perm) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm); else next.add(perm);
      return next;
    });
  };

  const toggleGroup = (groupPerms) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allChecked = groupPerms.every((p) => next.has(p.name));
      if (allChecked) groupPerms.forEach((p) => next.delete(p.name));
      else groupPerms.forEach((p) => next.add(p.name));
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!name.trim()) next.name = t('validation.required');
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), description: description.trim(), permissions: Array.from(selected) });
    } finally {
      setSubmitting(false);
    }
  };

  const isSystem = initial?.system;

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? t('roles_page.edit_role') : t('roles_page.new_role')}
      size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row form-row--2">
          <FormField label={t('roles_page.field_name')} htmlFor="role-name" required error={errors.name}>
            <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} disabled={isSystem} invalid={Boolean(errors.name)} />
          </FormField>
          <FormField label={t('roles_page.field_description')} htmlFor="role-desc">
            <Input id="role-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
        </div>

        <div className="form-label" style={{ marginBottom: '8px' }}>{t('roles_page.field_permissions')}</div>
        <div className="perm-grid">
          {grouped.map(([group, perms]) => {
            const allChecked = perms.every((p) => selected.has(p.name));
            const someChecked = perms.some((p) => selected.has(p.name));
            return (
              <div key={group} className="perm-group">
                <label className={classNames('perm-group-head', allChecked && 'is-all', someChecked && !allChecked && 'is-some')}>
                  <input type="checkbox" checked={allChecked}
                         ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                         onChange={() => toggleGroup(perms)} />
                  <span>{group}</span>
                </label>
                <div className="perm-list">
                  {perms.map((p) => (
                    <label key={p.name} className="perm-item">
                      <input type="checkbox" checked={selected.has(p.name)} onChange={() => toggle(p.name)} />
                      <span className="perm-item-name">{p.name.split(':')[1] || p.name}</span>
                      {p.description && <span className="perm-item-desc">{p.description}</span>}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </form>
    </Modal>
  );
}
